import React, { useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import globalStyle from "../../styles/globalStyle";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  position?: "left" | "right";
  size?: "sm" | "md" | "lg" | "xl";
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = "right",
  size = "md",
  closeOnBackdropClick = true,
  showCloseButton = true,
  className = "",
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const sizeClasses = {
    sm: "w-full max-w-sm",
    md: "w-full max-w-md",
    lg: "w-full max-w-lg",
    xl: "w-full max-w-xl",
  }[size];

  const positionClasses = {
    left: "left-0",
    right: "right-0",
  }[position];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        aria-hidden="true"
        onClick={closeOnBackdropClick ? onClose : undefined}
      />

      {/* Drawer Container */}
      <div className={`fixed inset-y-0 ${positionClasses} flex max-w-full`}>
        <div
          className={`${sizeClasses} h-full transform transition-transform duration-300 ${
            isOpen
              ? "translate-x-0"
              : position === "right"
              ? "translate-x-full"
              : "-translate-x-full"
          } ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`flex h-full flex-col bg-gray-900 ${globalStyle.shadowLg}`}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div
                className={`flex items-center justify-between px-6 py-4 border-b ${globalStyle.border}`}
              >
                {title && <h3 className={globalStyle.h5}>{title}</h3>}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="ml-4 rounded-lg p-2 hover:bg-gray-800 transition-colors duration-200"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 bg-black">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drawer;
